import { useState } from "react";
import { Box, IconButton, Skeleton, Tooltip, Typography } from "@mui/material";
import GrassOutlinedIcon from "@mui/icons-material/GrassOutlined";
import PersonOutlinedIcon from "@mui/icons-material/PersonOutlined";
import WheatIcon from "./icons/WheatIcon";
import WaterDropOutlinedIcon from "@mui/icons-material/WaterDropOutlined";
import SquareFootOutlinedIcon from "@mui/icons-material/SquareFootOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import GppMaybeOutlinedIcon from "@mui/icons-material/GppMaybeOutlined";
import ContentCopyOutlinedIcon from "@mui/icons-material/ContentCopyOutlined";
import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import { formatArea } from "../utils/areaUnits";
import { useTranslation } from "react-i18next";
import { dateLocale, useLocalize } from "../i18n";

const ACCENTS = {
  field: { main: "var(--primary)", tint: "rgba(var(--primary-rgb), 0.1)" },
  owner: { main: "#b26a00", tint: "rgba(255, 152, 0, 0.14)" },
};

const formatDate = (ymd, language) =>
  ymd
    ? new Date(`${ymd}T00:00:00`).toLocaleDateString(dateLocale(language), {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "—";

export function ComplianceBadge({ compliant }) {
  const { t } = useTranslation();
  const Icon = compliant ? VerifiedOutlinedIcon : GppMaybeOutlinedIcon;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.25,
        borderRadius: "999px",
        fontSize: 11,
        fontWeight: 600,
        whiteSpace: "nowrap",
        color: compliant ? "#2e7d32" : "#c62828",
        bgcolor: compliant ? "#e8f5e9" : "#fdecea",
      }}
    >
      <Icon sx={{ fontSize: 14 }} />
      {compliant ? t("terms.compliant") : t("terms.notCompliant")}
    </Box>
  );
}

function StatTile({ icon: Icon, label, value }) {
  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 0,
        p: 1,
        borderRadius: "8px",
        bgcolor: "#f5f7fa",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.5,
          color: "text.secondary",
          fontSize: 11,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
        {label}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 600, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, py: 0.5 }}>
      <Icon sx={{ fontSize: 16, color: "text.secondary" }} />
      <Typography sx={{ fontSize: 12, color: "text.secondary", flex: 1 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 500, textAlign: "right" }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function CopyId({ id }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(id).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.5,
        mt: 1,
        pt: 1,
        borderTop: "1px solid #eef1f4",
      }}
    >
      <Typography
        sx={{
          flex: 1,
          minWidth: 0,
          fontSize: 10,
          color: "text.disabled",
          fontFamily: "monospace",
        }}
      >
        <Box
          component="span"
          sx={{
            fontFamily: 'Poppins, "IBM Plex Sans Arabic", sans-serif',
            fontWeight: 600,
            color: "text.secondary",
            mr: 0.5,
          }}
        >
          {t("terms.id")}
        </Box>
        {/* Kept whole: when it doesn't fit beside the label it moves to its own line instead of breaking. */}
        <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
          {id}
        </Box>
      </Typography>
      <Tooltip title={copied ? t("terms.copied") : t("terms.copyId")}>
        <IconButton
          size="small"
          onClick={copy}
          aria-label={t("terms.copyId")}
          sx={{ p: 0.25 }}
        >
          {copied ? (
            <CheckOutlinedIcon sx={{ fontSize: 14, color: "success.main" }} />
          ) : (
            <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
          )}
        </IconButton>
      </Tooltip>
    </Box>
  );
}

function PopupCard({ kind, title, compliant, children, id, actions, width }) {
  const { t } = useTranslation();
  const loading = id === undefined;
  const accent = ACCENTS[kind];
  const Icon = kind === "field" ? GrassOutlinedIcon : PersonOutlinedIcon;
  return (
    <Box sx={{ width: width ?? 264, fontFamily: "Poppins, sans-serif" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 1.25 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            color: accent.main,
            bgcolor: accent.tint,
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1, pr: actions ? 0 : 2 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: accent.main,
            }}
          >
            {kind === "field" ? t("terms.field") : t("terms.owner")}
          </Typography>
          <Typography
            noWrap
            title={title}
            sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}
          >
            {loading ? <Skeleton width="70%" /> : title || "—"}
          </Typography>
        </Box>
        {actions}
      </Box>
      <Box sx={{ mb: 1.25 }}>
        {loading ? (
          <Skeleton variant="rounded" width={96} height={21} />
        ) : (
          <ComplianceBadge compliant={compliant} />
        )}
      </Box>
      {children}
      {loading ? (
        <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #eef1f4" }}>
          <Skeleton height={18} />
        </Box>
      ) : (
        <CopyId id={id} />
      )}
    </Box>
  );
}

const capitalize = (text) =>
  text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export function PopupError() {
  const { t } = useTranslation();
  return (
    <Typography sx={{ fontSize: 13, color: "error.main", py: 0.5, pr: 2 }}>
      {t("popup.loadFailed")}
    </Typography>
  );
}

// Without data, the cards show their layout with placeholder bars.
const bar = <Skeleton width={72} sx={{ display: "inline-block" }} />;

export function FieldCard({ field, areaUnit, actions, width }) {
  const { t, i18n } = useTranslation();
  const localize = useLocalize();
  const language = i18n.resolvedLanguage;
  const show = (value) => (field ? value : bar);
  return (
    <PopupCard
      kind="field"
      title={field?.owner}
      compliant={field?.compliant}
      id={field?.id}
      actions={actions}
      width={width}
    >
      <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
        <StatTile
          icon={SquareFootOutlinedIcon}
          label={t("terms.area")}
          value={show(formatArea(field?.area, areaUnit))}
        />
        <StatTile
          icon={WheatIcon}
          label={t("terms.crop")}
          value={show(capitalize(localize(field, "crop")) || "—")}
        />
      </Box>
      <InfoRow
        icon={EventOutlinedIcon}
        label={t("terms.season")}
        value={show(
          `${formatDate(field?.sos, language)} – ${formatDate(field?.eos, language)}`,
        )}
      />
      <InfoRow
        icon={WaterDropOutlinedIcon}
        label={t("terms.irrigation")}
        value={show(localize(field, "irrigation_method"))}
      />
      <InfoRow
        icon={PhoneOutlinedIcon}
        label={t("terms.phone")}
        value={show(field?.phone)}
      />
    </PopupCard>
  );
}

export function OwnerCard({ owner, areaUnit }) {
  const { t } = useTranslation();
  const show = (value) => (owner ? value : bar);
  return (
    <PopupCard
      kind="owner"
      title={owner?.name}
      compliant={owner?.compliant}
      id={owner?.id}
    >
      <Box sx={{ display: "flex", gap: 1 }}>
        <StatTile
          icon={SquareFootOutlinedIcon}
          label={t("terms.area")}
          value={show(formatArea(owner?.area, areaUnit))}
        />
        <StatTile
          icon={PhoneOutlinedIcon}
          label={t("terms.phone")}
          value={show(owner?.phone || "—")}
        />
      </Box>
    </PopupCard>
  );
}
